import { ArrowsInSimpleIcon } from '@phosphor-icons/react';
import type Konva from 'konva';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Layer, Line, Stage } from 'react-konva';

import { diffElement, overlayLayersToElements } from './variant-bridge';

import { KonvaLogoNode } from '@/components/create/v2/sections/media-studio/text-editor/KonvaLogoNode';
import { KonvaShapeNode } from '@/components/create/v2/sections/media-studio/text-editor/KonvaShapeNode';
import { KonvaTextNode } from '@/components/create/v2/sections/media-studio/text-editor/KonvaTextNode';
import {
  type AnyElement,
  getZIndex,
  isLogoElement,
  isShapeElement,
  useTextEditorStore,
} from '@/components/create/v2/sections/media-studio/text-editor/useTextEditorStore';
import { clampPan } from '@/lib/concept-workbench/pan';
import { stageFor } from '@/lib/concept-workbench/stage-dimensions';
import type { AspectRatioToken } from '@/lib/concept-workbench/types';
import { useVariantSetStore } from '@/lib/concept-workbench/useVariantSetStore';
import { cn } from '@/lib/utils';

/**
 * THE CANVAS. Mounted against the SELECTED VARIANT, over the existing engine.
 *
 * HOW THIS REUSES THE ENGINE RATHER THAN FORKING IT.
 * `MediaStudioTextOverlay` is the built editor — but it is inseparable from
 * `EditModeShell` (its own full-screen chrome, its own title bar, its own
 * Done/Cancel) and from `TextOverlayToolbar` (a horizontal property bar that
 * would duplicate this surface's right inspector). It also lives under
 * `src/components/create/v2/**`, the protected Home-composer tree, so it cannot
 * be refactored to expose a bare stage.
 *
 * So the parts that ARE the engine are imported and used verbatim: the three
 * Konva nodes (transformer chrome, drag, resize, the corner-drag font-scale
 * logic, text rasterisation) and their store. What is re-implemented here is the
 * ~60 lines of container glue — wheel-zoom-to-cursor, pan, midpoint snap guides —
 * which is not the engine, and which cannot be shared without editing a protected
 * file. Every one of those behaviours is deliberately identical to the original,
 * including the zoom clamp and the snap threshold, so muscle memory transfers.
 *
 * THE STAGE IS NATIVE-SIZED, NOT SCREEN-SIZED. Konva's `scaleX/scaleY` carry the
 * fit, so every element coordinate in the store is in the ratio's real delivery
 * pixels (1080×1350 for 4:5, and so on). That is what `materialiseVariant`
 * produces, what `concept_renditions.overlay_layers` persists, and what the
 * server-side compositor renders — one coordinate space end to end. Sizing the
 * stage to the viewport instead would put a fourth, transient space in the middle
 * and every window resize would rewrite the user's geometry.
 *
 * THE TEXT-EDITOR STORE IS A WORKING BUFFER, NOT A SECOND SOURCE OF TRUTH. It
 * holds the materialised elements for the selected variant only. Edits flow out
 * through `diffElement`, which routes geometry per-ratio and everything else
 * through the propagating path. Re-hydration compares by VALUE, so an edit that
 * round-trips to the same materialised output does not re-enter the buffer — the
 * loop is self-stabilising rather than guarded by an origin flag that a future
 * code path would forget to set.
 */

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 4;
const NOOP_ZOOM_CHANGE = () => {};

export interface WorkbenchStageProps {
  aspectRatio: AspectRatioToken;
  imageUrl: string | null;
  variantId: string;
  /**
   * Mobile. Pan and zoom stay; transform handles and drag do not. A 44px thumb
   * over a 7px anchor is not an editing surface, and shipping one that looks
   * editable and is not would be a false pass.
   */
  readOnly?: boolean;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
  className?: string;
  /**
   * 'editor' (default): the interactive Design-mode canvas — zoom/pan, snap
   * guides, transformer chrome, the top-left fit/reset readout, all of it.
   *
   * 'composite': a chromeless, non-interactive render of ONLY the overlay
   * layers, used by `ConceptPreviewPane` to composite the same Konva geometry
   * on top of the real image `MediaSlot` already renders (via MediaSlot's own
   * `overlay` prop). No zoom/pan handlers, no snap guides, no transformer
   * chrome, no fit/reset control — `readOnly` is implied regardless of the
   * `readOnly` prop. The fit calculation subtracts ZERO chrome padding (the
   * editor's 48×96 is real UI it doesn't have) so the stage box is exactly the
   * container box, pixel-for-pixel with the image beneath it. ALWAYS called
   * with `imageUrl={null}` — the real image is the sibling `MediaSlot`, so
   * this mode never renders the editor's dashed empty-state box either; doing
   * so would paint an opaque rectangle over that image.
   */
  presentation?: 'editor' | 'composite';
}

export function WorkbenchStage({
  aspectRatio,
  imageUrl,
  variantId,
  readOnly = false,
  zoom: zoomProp,
  onZoomChange: onZoomChangeProp,
  className,
  presentation = 'editor',
}: WorkbenchStageProps): JSX.Element {
  const isComposite = presentation === 'composite';
  const effectiveReadOnly = isComposite ? true : readOnly;
  const zoom = isComposite ? 1 : (zoomProp ?? 1);
  const onZoomChange = onZoomChangeProp ?? NOOP_ZOOM_CHANGE;
  const native = stageFor(aspectRatio);
  const containerRef = useRef<HTMLDivElement>(null);
  const stageWrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);

  const [box, setBox] = useState({ width: 0, height: 0 });
  const [container, setContainer] = useState({ width: 0, height: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(
    null,
  );
  const [snapGuides, setSnapGuides] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const snapGuidesRef = useRef(snapGuides);

  const elements = useTextEditorStore((state) => state.elements);
  const selectedId = useTextEditorStore((state) => state.selectedId);
  const loadElements = useTextEditorStore((state) => state.loadElements);
  const selectElement = useTextEditorStore((state) => state.selectElement);

  const materialise = useVariantSetStore((state) => state.materialise);
  const document = useVariantSetStore((state) => state.document);
  const updateGeometry = useVariantSetStore((state) => state.updateGeometry);
  const updateSetLayer = useVariantSetStore((state) => state.updateSetLayer);
  const selectLayers = useVariantSetStore((state) => state.selectLayers);

  // Fit: the stage box is the largest native-aspect rectangle inside the
  // column. Composite mode has none of the editor's own chrome (no floating
  // prompt bar, no fit/reset readout) sharing the box, so it fits the FULL
  // container rather than subtracting the editor's 48×96 padding reserve —
  // subtracting that here would leave the overlay smaller than the image
  // MediaSlot renders underneath it and the two would visibly disagree.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = () => {
      const rect = node.getBoundingClientRect();
      setContainer({ width: rect.width, height: rect.height });
      const available = {
        width: Math.max(rect.width - (isComposite ? 0 : 48), 0),
        height: Math.max(rect.height - (isComposite ? 0 : 96), 0),
      };
      if (available.width <= 0 || available.height <= 0) return;
      const scale = Math.min(available.width / native.width, available.height / native.height);
      setBox({ width: native.width * scale, height: native.height * scale });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [native.width, native.height, isComposite]);

  // Hydrate the buffer from the set document, by value.
  const materialisedRef = useRef<string>('');
  useEffect(() => {
    if (!document) return;
    const next = overlayLayersToElements(materialise(variantId));
    const signature = JSON.stringify(next);
    if (signature === materialisedRef.current) return;
    materialisedRef.current = signature;
    loadElements(next);
  }, [document, materialise, variantId, loadElements]);

  // Buffer → set document. `previousRef` is the buffer's own last state, so a
  // drag emits only the keys the drag moved.
  const previousRef = useRef<Map<string, AnyElement>>(new Map());
  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = new Map(elements.map((element) => [element.id, element]));
    if (previous.size === 0) return;

    for (const element of elements) {
      const before = previous.get(element.id);
      if (!before || before === element) continue;
      const diff = diffElement(before, element, aspectRatio);
      if (Object.keys(diff.geometry).length > 0) {
        updateGeometry(variantId, element.id, diff.geometry);
      }
      if (Object.keys(diff.propagating).length > 0) {
        updateSetLayer(element.id, diff.propagating);
      }
    }
  }, [elements, aspectRatio, variantId, updateGeometry, updateSetLayer]);

  // Selection is shared: the layers panel and the canvas must never disagree
  // about what the inspector is showing.
  useEffect(() => {
    selectLayers(selectedId ? [selectedId] : []);
  }, [selectedId, selectLayers]);

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      const wrap = stageWrapRef.current;
      const nextZoom = Math.min(Math.max(ZOOM_MIN, zoom - event.deltaY * 0.0015), ZOOM_MAX);
      if (nextZoom === zoom) return;
      if (wrap) {
        const rect = wrap.getBoundingClientRect();
        const cx = event.clientX - (rect.left + rect.width / 2);
        const cy = event.clientY - (rect.top + rect.height / 2);
        const ratio = nextZoom / zoom;
        setPan((p) =>
          clampPan(
            { x: p.x * ratio + cx * (1 - ratio), y: p.y * ratio + cy * (1 - ratio) },
            box,
            container,
            nextZoom,
          ),
        );
      }
      onZoomChange(nextZoom);
    },
    [zoom, onZoomChange, box, container],
  );

  const handlePanStart = useCallback(
    (event: React.MouseEvent) => {
      if (zoom <= 1) return;
      const target = event.target as HTMLElement;
      if (target.closest('[contenteditable="true"], textarea, input')) return;
      panRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        baseX: pan.x,
        baseY: pan.y,
      };
    },
    [zoom, pan.x, pan.y],
  );

  const handlePanMove = useCallback(
    (event: React.MouseEvent) => {
      const anchor = panRef.current;
      if (!anchor) return;
      setPan(
        clampPan(
          {
            x: anchor.baseX + (event.clientX - anchor.startX),
            y: anchor.baseY + (event.clientY - anchor.startY),
          },
          box,
          container,
          zoom,
        ),
      );
    },
    [box, container, zoom],
  );

  const handlePanEnd = useCallback(() => {
    panRef.current = null;
  }, []);

  useEffect(() => {
    if (zoom <= 1) setPan({ x: 0, y: 0 });
  }, [zoom]);

  const scale = box.width > 0 ? box.width / native.width : 1;
  const snapThreshold = 5 / scale;

  const handleElementDragMove = useCallback(
    (
      _id: string,
      x: number,
      y: number,
      width: number,
      height: number,
      node: { x: (v: number) => void; y: (v: number) => void },
    ) => {
      const midX = native.width / 2;
      const midY = native.height / 2;
      const next = { x: null as number | null, y: null as number | null };
      if (Math.abs(x + width / 2 - midX) < snapThreshold) {
        next.x = midX;
        node.x(midX - width / 2);
      }
      if (Math.abs(y + height / 2 - midY) < snapThreshold) {
        next.y = midY;
        node.y(midY - height / 2);
      }
      const previous = snapGuidesRef.current;
      if (previous.x !== next.x || previous.y !== next.y) {
        snapGuidesRef.current = next;
        setSnapGuides(next);
      }
    },
    [native.width, native.height, snapThreshold],
  );

  const handleDragEndGuides = useCallback(() => {
    snapGuidesRef.current = { x: null, y: null };
    setSnapGuides({ x: null, y: null });
  }, []);

  const handleStageClick = useCallback(
    (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (event.target === event.target.getStage()) selectElement(null);
    },
    [selectElement],
  );

  const guideStroke = 'hsl(var(--primary))';

  /**
   * The `4:5 · 100%` line used to be dead text sitting in the page's flow with a
   * `pb-24` magic number holding it clear of the prompt bar. It is now the view
   * control it always looked like: at fit it stays a quiet readout, and the
   * moment the view is zoomed or panned it becomes the one control that gets you
   * back. Rendering a button that does nothing at rest would be the silent no-op
   * this repo keeps shipping, so the affordance appears only when it can act.
   */
  const isAtFit = zoom === 1 && pan.x === 0 && pan.y === 0;
  const resetView = useCallback(() => {
    setPan({ x: 0, y: 0 });
    onZoomChange(1);
  }, [onZoomChange]);

  return (
    <div
      ref={containerRef}
      onWheel={isComposite ? undefined : handleWheel}
      onMouseDown={isComposite ? undefined : handlePanStart}
      onMouseMove={isComposite ? undefined : handlePanMove}
      onMouseUp={isComposite ? undefined : handlePanEnd}
      onMouseLeave={isComposite ? undefined : handlePanEnd}
      className={cn(
        'relative flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden',
        isComposite && 'pointer-events-none absolute inset-0',
        className,
      )}
      style={!isComposite && zoom > 1 ? { cursor: 'grab' } : undefined}
    >
      <div
        ref={stageWrapRef}
        className="relative"
        style={{
          width: box.width || undefined,
          height: box.height || undefined,
          transform: isComposite ? undefined : `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center',
        }}
      >
        {!isComposite ? (
          imageUrl ? (
            <img
              src={imageUrl}
              alt=""
              crossOrigin="anonymous"
              className="absolute inset-0 size-full rounded-sm object-cover"
            />
          ) : (
            <div className="absolute inset-0 rounded-sm border border-dashed border-border/40 bg-muted/20" />
          )
        ) : null}

        {box.width > 0 ? (
          <div className="absolute inset-0 z-10 touch-none">
            <Stage
              ref={stageRef}
              width={box.width}
              height={box.height}
              scaleX={scale}
              scaleY={scale}
              onMouseDown={isComposite ? undefined : handleStageClick}
              onTouchStart={isComposite ? undefined : handleStageClick}
            >
              <Layer listening={!effectiveReadOnly}>
                {[...elements]
                  .sort((a, b) => getZIndex(a) - getZIndex(b))
                  .map((element) =>
                    isLogoElement(element) ? (
                      <KonvaLogoNode
                        key={element.id}
                        element={element}
                        isSelected={!effectiveReadOnly && selectedId === element.id}
                        onDragMove={handleElementDragMove}
                        onDragEndGuides={handleDragEndGuides}
                      />
                    ) : isShapeElement(element) ? (
                      <KonvaShapeNode
                        key={element.id}
                        element={element}
                        isSelected={!effectiveReadOnly && selectedId === element.id}
                        onDragMove={handleElementDragMove}
                        onDragEndGuides={handleDragEndGuides}
                      />
                    ) : (
                      <KonvaTextNode
                        key={element.id}
                        element={element}
                        isSelected={!effectiveReadOnly && selectedId === element.id}
                        isEditing={false}
                        onDoubleClick={() => {}}
                        onDragMove={handleElementDragMove}
                        onDragEndGuides={handleDragEndGuides}
                      />
                    ),
                  )}
              </Layer>
              {!isComposite ? (
                <Layer listening={false}>
                  {snapGuides.x !== null ? (
                    <Line
                      points={[snapGuides.x, 0, snapGuides.x, native.height]}
                      stroke={guideStroke}
                      strokeWidth={1 / scale}
                    />
                  ) : null}
                  {snapGuides.y !== null ? (
                    <Line
                      points={[0, snapGuides.y, native.width, snapGuides.y]}
                      stroke={guideStroke}
                      strokeWidth={1 / scale}
                    />
                  ) : null}
                </Layer>
              ) : null}
            </Stage>
          </div>
        ) : null}
      </div>

      {/* TOP-left, not bottom-left. Bottom-left is where a zoom pill belongs in
          `EditModeShell`, but this canvas has a floating prompt bar whose box is
          86% of the column and centred, so its transparent left edge sat over
          the control and swallowed the click — caught by driving the real route,
          invisible to every other check. The top of the stage is the only corner
          nothing else claims. Composite mode renders none of this — it is
          chromeless by contract, not merely visually quiet. */}
      {!isComposite ? (
        <div className="pointer-events-none absolute left-4 top-4 z-20">
          {isAtFit ? (
            <span className="text-micro text-muted-foreground">
              {aspectRatio} · 100%
              {readOnly ? ' · view only on this screen size' : ''}
            </span>
          ) : (
            <button
              type="button"
              onClick={resetView}
              className={cn(
                'pointer-events-auto inline-flex items-center gap-1.5 rounded-full',
                'bg-card/80 px-2.5 py-1 backdrop-blur-md',
                'text-micro font-medium text-foreground/85 transition-colors hover:text-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
            >
              <ArrowsInSimpleIcon aria-hidden className="size-3" weight="regular" />
              {aspectRatio} · {Math.round(zoom * 100)}% · Fit
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
